import {MediaMessageDTO, MediaMessageDTOType, MessageDTO} from '@web-libs/features/chat/api/dto/chat-message-dto.types';
import {TrmzFileDTOConverter} from '@web-libs/features/files/trmz-file/api/trmz-file-dto-converter';
import {LocalFileCache} from '@web-libs/features/chat/api/local-file-cache/local-file-cache';
import {
    ChatMessage,
    ChatMessageType,
    SendMediumMessage,
    SendMessage,
    SendTextMessage, SendVoiceMessage
} from '@web-libs/features/chat/domain/types/chat-message.types';
import { SupportedLanguage } from '@web-libs/core/internationalization/internationalization.types';

const fileMessagePrefix = '###TRMZ_TRESSENGER_MEDIA@@@@@_____';
const _isFileEncodedMessage = (message: string): boolean => message.startsWith(fileMessagePrefix);
const _decodeFileMessage = (message: string): MediaMessageDTO => {
    const split = message.split(fileMessagePrefix);
    const json = split[split.length - 1];
    return JSON.parse(json);
};

export const parseSendDate = (sendDate?: string | number[]) => {
    if (!sendDate) {
        return new Date();
    }
    if (typeof sendDate === 'string') {
        return new Date(sendDate.endsWith('Z') ? sendDate : sendDate + 'Z');
    }
    return new Date(Date.UTC(sendDate[0],
                             sendDate[1] - 1,
                             sendDate[2],
                             sendDate[3],
                             sendDate[4],
                             sendDate[5]));
};

export const parseChatMessage = (message: MessageDTO,
                                 ownUserId: string,
                                 converter: TrmzFileDTOConverter,
                                 localFileCache: LocalFileCache): ChatMessage => {
    const messageId = message.messageId;
    const senderId = message.senderId ?? message.userId;
    const senderName = message.senderName ?? message.sender;
    const sendDate = parseSendDate(message.sendDate);
    const {sourceLanguage, language} = message;

    const stringToParse = message.message ?? message.content ?? message.msg ?? JSON.stringify({msg: ''});
    const contentDTO = JSON.parse(stringToParse);

    const localReferenceId = contentDTO.localReferenceId;
    const _innerMessage = contentDTO.msg;
    const {fileType, fileId, fileRoomId, duration, aspectRatio} = parseMediaMessage(_innerMessage);
    const localFileUrl = localFileCache.getCachedFileUrlByIds(localReferenceId,
                                                              messageId);

    return {
        messageId,
        type: parseFileTypeToChatMessageType(fileType),
        text: _isFileEncodedMessage(_innerMessage) ? undefined : _innerMessage,
        contentUrl: localFileUrl ?? (fileId && fileRoomId ? parseFile(fileId,
                                                                      fileRoomId,
                                                                      converter) : undefined),
        senderName: senderName ?? 'Unbekannt',
        senderId: senderId ?? 'Unbekannt',
        sendDate,
        ownMessage: ownUserId === senderId,
        deliveryStatus: 'delivered',
        internalReferenceId: localReferenceId,
        dimensions: aspectRatio ? {
            width: 500,
            height: 500 / (aspectRatio ?? 1)
        } : undefined,
        sourceLanguage,
        language,
    };
};

const parseFileTypeToChatMessageType = (fileType?: MediaMessageDTOType): ChatMessageType => {
    switch (fileType) {
        case 'IMAGE':
            return 'medium';
        case 'VOICE':
            return 'voice';
        case 'FILE':
            return 'file';
        default:
            return 'text';
    }
};

const parseFile = (fileId: string,
                   roomId: string,
                   converter: TrmzFileDTOConverter): string => {
    return converter.getChatUrl(fileId,
                                roomId);
};

const parseMediaMessage = (message: string): {
    fileType?: MediaMessageDTOType,
    fileId?: string,
    fileRoomId?: string,
    duration?: number,
    aspectRatio?: number
} => {
    const _isFileMessage = _isFileEncodedMessage(message);
    if (!_isFileMessage) {
        return {};
    }
    const mediaMessageDTO = _decodeFileMessage(message);
    const fileType = mediaMessageDTO.type;
    const fileId = mediaMessageDTO.fileStorageId;
    const fileRoomId = mediaMessageDTO.roomId;
    const duration = mediaMessageDTO.duration;
    const aspectRatio = mediaMessageDTO.prop;
    return {fileType, fileId, fileRoomId, duration, aspectRatio};
};

const createMessagePayload = (msg: string,
                              internalReferenceId: string,
                              ownUserId: string, language?: SupportedLanguage) => {
    return {
        messageType: 'CHAT',
        localReferenceId: internalReferenceId,
        sender: '',
        senderId: ownUserId,
        msg: msg,
        lang: language
    };
};

export const encodeTextMessage = (sendTextMessage: SendTextMessage,
                                  ownUserId: string) => {
    const payload = createMessagePayload(sendTextMessage.text,
                                         sendTextMessage.internalReferenceId!,
                                         ownUserId,
                                         sendTextMessage.sourceLanguage);
    return JSON.stringify(payload);
};

export const encodeMediaMessage = (sendMessage: SendMessage,
                                   ownUserId: string,
                                   uploadedFileId: string,
                                   chatroomId: string) => {

    const mediaType = _getMediaType(sendMessage);

    let aspectRatio: number | undefined;

    if ((sendMessage as SendMediumMessage).aspectRatio) {
        aspectRatio = (sendMessage as SendMediumMessage).aspectRatio;
    }

    let duration: number | undefined;

    if((sendMessage as SendVoiceMessage).duration) {
        duration = (sendMessage as SendVoiceMessage).duration;
    }

    const mediaMessageDTO: MediaMessageDTO = {
        type: mediaType, // implement
        fileStorageId: uploadedFileId,
        roomId: chatroomId,
        prop: aspectRatio,
        duration: duration,
        loc: 'REMOTE',
    };

    const encodedMediaMessage = fileMessagePrefix + JSON.stringify(mediaMessageDTO);
    const payload = createMessagePayload(encodedMediaMessage,
                                         sendMessage.internalReferenceId!,
                                         ownUserId);
    return JSON.stringify(payload);
};

const _getMediaType =
    (sendMessage: SendMessage): MediaMessageDTOType => {
        switch (sendMessage.type) {
            case 'file':
                return 'FILE';
            case 'medium':
                return 'IMAGE';
            case 'voice':
                return 'VOICE';
            default:
                return 'FILE';
        }
    };
